import React, { useMemo, useState } from 'react';
import { Plus, Edit, Trash2, Stethoscope, Settings } from 'lucide-react';
import { SearchBar } from '../shared/SearchBar';
import { SortableTableHeader } from '../shared/SortableTableHeader';
import { Pagination } from '../shared/Pagination';
import { FormModal } from '../shared/FormModal';
import { ConfirmModal } from '../../../framework/modals/ConfirmModal';
import { ManageCategoriesModal } from '../shared/ManageCategoriesModal';
import { DraggableNotesList } from '../shared/DraggableNotesList';
import { FormInput } from '../shared/FormInput';
import { FormSelect } from '../shared/FormSelect';
import { FormTextarea } from '../shared/FormTextarea';
import { useConditionManagement } from '../hooks/useConditionManagement';
import { useModeratorData } from '../hooks/useModeratorData';
import { useTableSort } from '../hooks/useTableSort';
import { useModeratorPermissions } from '../hooks/useModeratorPermissions';

export const ConditionsSection: React.FC = () => {
  const { conditions, refetchConditions, refetchModeratorData } = useModeratorData();
  const condition = useConditionManagement(refetchConditions, refetchModeratorData);
  const { canManageConditions } = useModeratorPermissions();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  const filteredConditions = useMemo(() => {
    return conditions.filter(cond => {
      const matchesSearch = cond.name.toLowerCase().includes(condition.searchTerm.toLowerCase());
      const matchesCategory = !selectedCategoryId || cond.categoryId === selectedCategoryId;
      return matchesSearch && matchesCategory;
    });
  }, [conditions, condition.searchTerm, selectedCategoryId]);

  const categoryOptions = useMemo(() => [
    ...condition.categories.map(c => ({ value: c.id, label: c.name })),
    { value: '__ADD_NEW__', label: 'Add New...' }
  ], [condition.categories]);

  const categoryFilterOptions = useMemo(() => [
    { value: '', label: 'All Categories' },
    ...condition.categories.map(c => ({ value: c.id, label: c.name }))
  ], [condition.categories]);

  const {
    paginatedData,
    sortField,
    sortDirection,
    currentPage,
    totalPages,
    itemsPerPage,
    handleSort,
    setCurrentPage,
    setItemsPerPage
  } = useTableSort(filteredConditions, 'name', 10);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Condition Management
        </h2>
        <div className="flex items-center space-x-2">
          {canManageConditions && (
          <button
            onClick={condition.handleOpenManageCategories}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
              e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
          >
            <Settings className="w-4 h-4" />
            <span>Manage Categories</span>
          </button>
          )}
          {canManageConditions && (
          <button
            onClick={condition.handleCreate}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'white'
            }}
          >
            <Plus className="w-4 h-4" />
            <span>Add Condition</span>
          </button>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-3">
        <div className="w-48">
          <select
            value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(e.target.value)}
            className="w-full pl-3 py-2 border rounded-lg focus:ring-2 transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-primary)',
              '--tw-ring-color': 'var(--color-accent)',
              paddingRight: '3rem',
            } as React.CSSProperties}
          >
            {categoryFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <SearchBar
            value={condition.searchTerm}
            onChange={condition.setSearchTerm}
            placeholder="Search conditions..."
          />
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
        totalItems={filteredConditions.length}
      />

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
              <SortableTableHeader
                field="name"
                label="Name"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableTableHeader
                field="category"
                label="Category"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th className="text-left p-3" style={{ color: 'var(--color-text-secondary)' }}>Description</th>
              <th className="text-right p-3" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((cond) => (
              <tr key={cond.id} className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                <td className="p-3" style={{ color: 'var(--color-text-primary)' }}>{cond.name}</td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>{cond.category?.name || '-'}</td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>{cond.description}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end space-x-2">
                    {canManageConditions && (
                    <button
                      onClick={() => condition.handleEdit(cond)}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: 'var(--color-accent)' }}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    )}
                    {canManageConditions && (
                    <button
                      onClick={() => condition.handleDelete(cond.id)}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: 'var(--color-error)' }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      <FormModal
        isOpen={condition.showForm}
        onClose={condition.handleCancel}
        title={condition.editing ? 'Edit Condition' : 'Add New Condition'}
        subtitle={condition.editing ? 'Update condition information' : 'Create a new medical condition'}
        icon={Stethoscope}
        maxWidth="lg"
      >
        <form onSubmit={condition.handleSubmit} className="space-y-4">
          <FormInput
            label="Name"
            required
            type="text"
            value={condition.form.name}
            onChange={(e) => condition.setForm({ ...condition.form, name: e.target.value })}
            placeholder="Enter condition name..."
            backgroundColor="primary"
          />

          <div>
            <FormSelect
              label="Category"
              value={condition.form.categoryId}
              onChange={(e) => condition.handleCategoryChange(e.target.value)}
              options={categoryOptions}
              placeholder="Select a category..."
              backgroundColor="primary"
            />
            {condition.showNewCategoryInput && (
              <div className="mt-2">
                <input
                  type="text"
                  value={condition.newCategoryName}
                  onChange={(e) => condition.setNewCategoryName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                  placeholder="Enter new category name..."
                  autoFocus
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    condition.handleCreateCategory();
                  }}
                  disabled={!condition.newCategoryName.trim() || condition.isCreatingCategory}
                  className="px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: 'var(--color-accent)',
                    color: 'white'
                  }}
                >
                  {condition.isCreatingCategory ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    condition.setShowNewCategoryInput(false);
                    condition.setNewCategoryName('');
                    // Reset categoryId if it was set to '__ADD_NEW__'
                    if (condition.form.categoryId === '__ADD_NEW__') {
                      condition.setForm({ ...condition.form, categoryId: '' });
                    }
                  }}
                  className="px-4 py-2 border rounded-lg transition-colors"
                  style={{
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <FormTextarea
            label="Description"
            value={condition.form.description}
            onChange={(e) => condition.setForm({ ...condition.form, description: e.target.value })}
            rows={3}
            placeholder="Enter description..."
            backgroundColor="primary"
          />

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Display Notes
            </label>
            <div className="space-y-2">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={condition.newNote}
                  onChange={(e) => condition.setNewNote(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      condition.handleAddNote();
                    }
                  }}
                  className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                  placeholder="Add a note..."
                />
                <button
                  type="button"
                  onClick={condition.handleAddNote}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: 'var(--color-accent)',
                    color: 'white'
                  }}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {condition.form.notes.length > 0 && (
                <DraggableNotesList
                  notes={condition.form.notes}
                  onReorder={condition.handleReorderNotes}
                  onDelete={condition.handleRemoveNote}
                />
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={condition.handleCancel}
              className="px-4 py-2 border rounded-lg transition-colors hover:bg-gray-50"
              style={{
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-secondary)'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={condition.isCreating || condition.isUpdating}
              className="px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              style={{
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-text-on-accent)'
              }}
            >
              {condition.isCreating || condition.isUpdating ? 'Saving...' : (condition.editing ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={condition.showDeleteModal}
        onClose={condition.handleCancelDelete}
        onConfirm={condition.handleConfirmDelete}
        title="Delete Condition"
        message="Are you sure you want to delete this condition? This action cannot be undone."
        type="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={condition.isDeleting}
      />

      {/* Manage Categories Modal */}
      {canManageConditions && (
      <ManageCategoriesModal
        isOpen={condition.showManageCategoriesModal}
        onClose={condition.handleCloseManageCategories}
        categories={condition.categoriesWithCounts}
        onDelete={condition.handleDeleteCategory}
        onCreate={condition.handleCreateCategory}
        isDeleting={condition.isDeletingCategory}
        isCreating={condition.isCreatingCategory}
      />
      )}
    </div>
  );
};

